import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Chip } from "@mui/material";
import PortalPage from '../../../components/portal/PortalPage'
import './administration.css';
import { RouteComponentProps } from 'react-router';
import { useProfile } from 'hooks/useRoles';
import { MakeActiveButtonText, MakeInactiveButtonText, RefreshButtonText, UserManagementModule, ViewOrEditButtonText, EmployeeTypeEmployeeLabel, EmployeeTypeContractorLabel } from './user-management-data';
import { IConfirmProps } from '../../ui/ConfirmationBox';
import { IModalBoxProps } from '../../ui/ModalBox';
import { DataGrid } from '../../ui/DataGrid';
import { userAdminApi } from '../../../shared/services/userApi';
import {
    AdminUserModel, AdminUserListRequest, UserStatusEnum, AdminUserSetStatusRequest,
    EmployeeTypeEnum, IUserSearchResult
} from '../../../shared/models/api/models';
import { IActionProps, Toolbar } from '../../ui/Toolbar';
import { ReactNode } from 'react';
import { LabelledCheckbox } from '../../ui/LabelledCheckbox';
import {
    MailOutlined, NearMeOutlined, PersonOutlined, RadioButtonCheckedOutlined,
    RadioButtonUncheckedOutlined, SupervisorAccountOutlined
} from '@mui/icons-material';
import { TabPanel } from '../../ui/TabPanel';
import { IListItemProps, List } from '../../ui/Lists';
import { UserProfile } from '../../../shared/models/site/user-profile';
import Spinner from '../../ui/Spinner';
import { DetailFormWrapper } from '../../ui/DetailForm';

interface IUserFilterUser {
    id: number,
    firstname: string,
    lastname: string,
    email: string,
    city: string,
    officeLocation: string,
    supervisor?: IUserSearchResult
}

const UserListItem = <T extends IUserFilterUser,>(props: IListItemProps<T>) => {
    return (
        <div className={"list-item user-list-item" + ((props.selected === true) ? " selected" : "")} onClick={() => props.toggle(props.item)}>
            <LabelledCheckbox checkedValue={props.selected}><span className="name">{props.item.firstname} {props.item.lastname}</span></LabelledCheckbox>
            <div className="second-line">
                {props.item.city &&
                    <span className="location"><NearMeOutlined /> {props.item.city}</span>
                }
                {props.item.supervisor &&
                    <span className="supervisor">
                        <span><SupervisorAccountOutlined />{props.item.supervisor.fullname}</span>
                    </span>
                }
                {props.item.city === null && props.item.email &&
                    <span className="email">
                        <span><MailOutlined />{props.item.email}</span>
                    </span>
                }
            </div>
        </div>
    );
};

const StringListItem = (props: IListItemProps<string>) => {
    return (
        <div className={"list-item " + (props.selected ? " selected" : "")} onClick={() => props.toggle(props.item)}>
            <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item}</span></LabelledCheckbox>
        </div>
    );
};

interface IUserFilterProps<T> {
    allUsers: T[],
    selected: T[],
    setSelected: (users: T[]) => void,
    allowSelectAll?: boolean,
    isSelected?: (usr: T) => true | false | 'partial'
}

const UserFilter = <T extends IUserFilterUser>(props: IUserFilterProps<T>) => {
    const [summaryText, setSummaryText] = useState("");
    const [filterText, setFilterText] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);

    const filterUsersAsync = async (name: string, users?: T[]): Promise<T[]> => {
        const lcName = name.toLowerCase();

        const result = await (users ?? []).filter(u => {
            return ((u.firstname + ' ' + u.lastname).toLowerCase().indexOf(lcName) > -1
                || (u.email && u.email.toLowerCase().indexOf(lcName) > -1));
        });

        const usersText = result.length === 1 ? "user" : "users";

        setSummaryText(`${result.length} ${usersText} found for search "${name}".`);

        return result;
    };

    const selectItems = (items: T[]) => {
        props.setSelected(props.allUsers.filter(usr => props.selected.includes(usr) || items.includes(usr)));
    };

    const deselectItems = (items: T[]) => {
        props.setSelected(props.selected.filter(usr => !items.includes(usr)));
    };

    const toggleUser = (usr: T) => {
        if (props.selected.includes(usr)) {
            props.setSelected(props.selected.filter(u => u !== usr));
        } else {
            props.setSelected(props.selected.concat(usr));
        }
    };

    return (
        <List<T>
            summary={summaryText}
            dataSource={props.allUsers}
            searchDelayMs={1}
            displayLoadingData={true}
            refreshFilterOnDataSourceChange={true}
            listItemType={UserListItem}
            filterText={filterText}
            setFilterText={setFilterText}
            expanded={isExpanded}
            selectItems={props.allowSelectAll ? selectItems : undefined}
            deselectItems={props.allowSelectAll ? deselectItems : undefined}
            maxItems={4}
            isSelected={props.isSelected}
            setExpanded={setIsExpanded}
            buildKey={(item: T) => { return item.id; }}
            filterAsync={filterUsersAsync}
            toggleSelected={toggleUser}
            noResultsMessage="No users found"
            filterTextPlaceholder="Enter first name, last name or email address"
            selected={props.selected} />
    );
};

interface IStringFilterProps {
    allValues: string[],
    selected: string[],
    setSelected: (values: string[]) => void,
    singularDesc: string,
    pluralDesc: string,
    placeholder: string
}

const StringFilter = (props: IStringFilterProps) => {
    const [summaryText, setSummaryText] = useState("");
    const [filterText, setFilterText] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);

    const filter = (searchText: string, values?: string[]) => {
        const lcName = searchText.toLowerCase();

        const result = (values ?? []).filter(v => v?.toLowerCase().indexOf(lcName) > -1);

        const singularOrPlural = result.length === 1 ? props.singularDesc : props.pluralDesc;

        setSummaryText(`${result.length} ${singularOrPlural} found for search "${searchText}".`);

        return result;
    };

    const toggleValue = (value: string) => {
        if (props.selected.includes(value)) {
            props.setSelected(props.selected.filter(s => s !== value));
        } else {
            props.setSelected(props.selected.concat(value));
        }
    };

    return (
        <List<string>
            summary={summaryText}
            dataSource={props.allValues}
            searchDelayMs={1}
            displayLoadingData={true}
            refreshFilterOnDataSourceChange={true}
            listItemType={StringListItem}
            filterText={filterText}
            setFilterText={setFilterText}
            expanded={isExpanded}
            setExpanded={setIsExpanded}
            buildKey={item => item}
            filter={filter}
            toggleSelected={toggleValue}
            noResultsMessage={"No " + props.pluralDesc + " found."}
            filterTextPlaceholder={props.placeholder}
            selected={props.selected} />
    );
};

interface IUsersByDepartmentFilterProps {
    allDepartments: string[],
    selectedDepartments: string[],
    setSelectedDepartments: (depts: string[]) => void
}

const UsersByDepartmentFilter = (props: IUsersByDepartmentFilterProps) => {
    return (
        <StringFilter
            placeholder="select a department from the list"
            pluralDesc="departments"
            singularDesc="department"
            allValues={props.allDepartments}
            selected={props.selectedDepartments}
            setSelected={props.setSelectedDepartments}
        />
    );
};

//TODO: refactor to ui folder, if we need radio buttons elsewhere with black accents
interface IRadioButtonProps {
    value: boolean,
    onClick: () => void,
    label: ReactNode
}

const RadioButton = (props: IRadioButtonProps) => {
    return (
        <div className="radio-button" onClick={props.onClick}>
            <span className="icon">{props.value ? <RadioButtonCheckedOutlined /> : <RadioButtonUncheckedOutlined />}</span>
            <span className="label">{props.label}</span>
        </div>
    );
};

interface IUsersByemployeeTypeFilterProps {
    selectedEmployeeType: EmployeeTypeEnum | null,
    setSelectedEmployeeType: (empType: EmployeeTypeEnum | null) => void
}

const UsersByEmployeeTypeFilter = (props: IUsersByemployeeTypeFilterProps) => {
    return (
        <div>
            <RadioButton
                value={props.selectedEmployeeType === null}
                onClick={() => props.setSelectedEmployeeType(null)}
                label="All"
            />

            <RadioButton
                value={props.selectedEmployeeType === EmployeeTypeEnum.Employee}
                onClick={() => props.setSelectedEmployeeType(EmployeeTypeEnum.Employee)}
                label={EmployeeTypeEmployeeLabel}
            />

            <RadioButton
                value={props.selectedEmployeeType === EmployeeTypeEnum.Contractor}
                onClick={() => props.setSelectedEmployeeType(EmployeeTypeEnum.Contractor)}
                label={EmployeeTypeContractorLabel}
            />
        </div>
    );
};

const distinct = <T,>(arr: T[]) => {
    return arr.filter((v, i, s) => { return s.indexOf(v) === i; });
};

interface IUserFiltersProps {
    allUsers: AdminUserModel[],
    selectedUsers: AdminUserModel[],
    setSelectedUsers: (users: AdminUserModel[]) => void,
    selectedSupervisors: AdminUserModel[],
    setSelectedSupervisors: (users: AdminUserModel[]) => void,
    allDepartments: string[],
    selectedDepartments: string[],
    setSelectedDepartments: (depts: string[]) => void,
    selectedEmployeeType: EmployeeTypeEnum | null,
    setSelectedEmployeeType: (empType: EmployeeTypeEnum | null) => void
    currentUser?: UserProfile,
    selectAllUsers: boolean,
    setSelectAllUsers: (val: boolean) => void
}

const ResultHeading = (props: { text: string, icon?: any }) => {
    const Icon = props.icon;

    return (
        <div className="results-heading-container">
            <div className="results-heading">
                {Icon && <span className="icon"><Icon /></span>}
                <span className="text">Viewing: {props.text}</span>
            </div>
        </div>
    );
};

const UserFilters = (props: IUserFiltersProps) => {
    const [userFilterTab, setUserFilterTab] = useState(0);
    const [supervisorIds, setSupervisorIds] = useState([] as number[]);

    useEffect(() => {
        setSupervisorIds(distinct((props.allUsers ?? []).filter(usr => usr.supervisor !== null).map(usr => usr.supervisor?.id ?? -1)));
    }, [props.allUsers]);

    const deselectUser = (userToRemove: AdminUserModel) => {
        props.setSelectedUsers(props.selectedUsers.filter(selectedUser => selectedUser !== userToRemove));
    };

    const deselectSupervisor = (supervisorToRemove: AdminUserModel) => {
        props.setSelectedSupervisors(props.selectedSupervisors.filter(selectedSupervisor => selectedSupervisor !== supervisorToRemove));
    };

    const deselectDepartment = (departmentToRemove: string) => {
        props.setSelectedDepartments(props.selectedDepartments.filter(selectedDepartment => selectedDepartment !== departmentToRemove));
    };

    const whenUserOrSupervisorIsSelected = (usr: AdminUserModel) => {
        if (props.selectedUsers.includes(usr)) {
            return true;
        }

        if (usr.supervisor === null) {
            return false;
        } else {
            if (props.selectedSupervisors.findIndex(sv => sv.id === usr.supervisor?.id) > -1) {
                return 'partial';
            }
        }

        return false;
    }

    return (
        <div className="filter-container">
            <div className="filter-heading">
                <span className="text">Select User</span>
            </div>

            <div className="filter-sub-heading" onClick={() => props.setSelectAllUsers(!props.selectAllUsers)}>
                <span className="icon"><PersonOutlined /></span>
                <span className="text">User</span>
            </div>

            <div className="filter-body">
                <div className="filter-row">
                    <Tabs className="user-filter-tabs" value={userFilterTab} onChange={(e, v) => { setUserFilterTab(v); }}>
                        <Tab label="Find a user" />
                        <Tab label="Users in my team" />
                        <Tab label="Users by supervisor" />
                        <Tab label="Users by department" />
                        <Tab label="Users by employee type" />
                    </Tabs>

                    <TabPanel value={userFilterTab} index={0}>
                        <UserFilter
                            allUsers={props.allUsers}
                            selected={props.selectedUsers}
                            setSelected={props.setSelectedUsers}
                            isSelected={whenUserOrSupervisorIsSelected}
                            allowSelectAll={true}
                        />
                    </TabPanel>

                    <TabPanel value={userFilterTab} index={1}>
                        {props.currentUser &&
                            <UserFilter
                                allUsers={props.allUsers.filter(usr => usr.supervisor?.id === props.currentUser?.id)}
                                selected={props.selectedUsers}
                                setSelected={props.setSelectedUsers}
                                isSelected={whenUserOrSupervisorIsSelected}
                                allowSelectAll={true}
                            />
                        }
                    </TabPanel>

                    <TabPanel value={userFilterTab} index={2}>
                        <UserFilter
                            allUsers={props.allUsers.filter(usr => supervisorIds.includes(usr.id))}
                            selected={props.selectedSupervisors}
                            setSelected={props.setSelectedSupervisors}
                            allowSelectAll={true}
                        />
                    </TabPanel>

                    <TabPanel value={userFilterTab} index={3}>
                        <UsersByDepartmentFilter
                            allDepartments={props.allDepartments}
                            selectedDepartments={props.selectedDepartments}
                            setSelectedDepartments={props.setSelectedDepartments} />
                    </TabPanel>

                    <TabPanel value={userFilterTab} index={4}>
                        <UsersByEmployeeTypeFilter
                            selectedEmployeeType={props.selectedEmployeeType}
                            setSelectedEmployeeType={props.setSelectedEmployeeType} />
                    </TabPanel>
                </div>
                <div className="filter-row filter-chips">
                    <span>
                        {props.selectedUsers.map(usr =>
                            <Chip key={usr.id}
                                onDelete={() => deselectUser(usr)}
                                label={<><b>User:</b> {usr.firstname} {usr.lastname}</>} />
                        )}

                        {props.selectedSupervisors.map(sv =>
                            <Chip key={sv.id}
                                onDelete={() => deselectSupervisor(sv)}
                                label={<><b>Supervisor:</b> {sv.firstname} {sv.lastname}</>} />
                        )}

                        {props.selectedDepartments.map(dpt =>
                            <Chip key={dpt}
                                onDelete={() => deselectDepartment(dpt)}
                                label={<><b>Department:</b> {dpt}</>} />
                        )}

                        {props.selectedEmployeeType === EmployeeTypeEnum.Employee &&
                            <Chip onDelete={() => props.setSelectedEmployeeType(null)}
                                label={<><b>Employee type:</b> BHP Employees users</>} />
                        }

                        {props.selectedEmployeeType === EmployeeTypeEnum.Contractor &&
                            <Chip onDelete={() => props.setSelectedEmployeeType(null)}
                                label={<><b>Employee type:</b> Contractors users</>} />
                        }
                    </span>
                </div>
            </div>
        </div>
    );
};

interface IUserManagementPageProps {
    module: string
}

const UserManagementPage = (props: RouteComponentProps<IUserManagementPageProps>) => {
    const currentUser = useProfile();

    const [loadingState, setLoadingState] = useState("init");
    const [confirmationProps, setConfirmationProps] = useState(null as IConfirmProps | null);
    const [modalProps, setModalProps] = useState(null as IModalBoxProps | null);
    const [allUsers, setAllUsers] = useState([] as AdminUserModel[]);
    const [selectedUsers, setSelectedUsers] = useState([] as AdminUserModel[]);
    const [selectAllUsers, setSelectAllUsers] = useState(true);

    const [selectedEmployeeType, setSelectedEmployeeType] = useState(null as EmployeeTypeEnum | null);

    const [allDepartments, setAllDepartments] = useState([] as string[]);
    const [filterDepartments, setFilterDepartments] = useState([] as string[]);

    // this is a subset of all users, with the filters applied
    const [filteredUsers, setFilteredUsers] = useState([] as AdminUserModel[]);

    // this is the list of users the users has elected to include in the grid
    const [filterUsers, setFilterUsers] = useState([] as AdminUserModel[]);

    // this is the list of users the supervisors has elected to include the subordinates of in the grid
    const [filterSupervisors, setFilterSupervisors] = useState([] as AdminUserModel[]);

    const refresh = async () => {
        setLoadingState("loading");

        const req = {} as AdminUserListRequest;

        userAdminApi.listAsync(req)
            .then(result => {
                setSelectedUsers(result.filter(updatedUser => selectedUsers.findIndex(selected => selected.id === updatedUser.id) > -1));
                setFilterUsers(result.filter(updatedUser => filterUsers.findIndex(selected => selected.id === updatedUser.id) > -1));
                setFilterSupervisors(result.filter(updatedUser => filterSupervisors.findIndex(selected => selected.id === updatedUser.id) > -1));
                setAllUsers(result);
                setAllDepartments(
                    distinct(result.map(usr => usr.department)).filter(dept => dept?.length > 0).sort()
                );
                setLoadingState("ready");
            })
            .catch(e => {
                setLoadingState("error");
                ask("There was an error loading the user data.", ["Retry", "Cancel"], "Retry", refresh);
            });
    };

    const replaceItem = (users: AdminUserModel[], updatedUser: AdminUserModel) => {
        // take a copy of the array
        const arr = users.map(u => u);

        const idx = arr.findIndex(u => u.id === updatedUser.id);

        if (idx > -1) {
            // replace the row with the updated user
            arr[idx] = updatedUser;
        }

        return arr;
    };

    const onDataChanged = (updatedUser: AdminUserModel) => {
        const idx = allUsers.findIndex(usr => usr.id === updatedUser.id);

        if (idx > -1) {
            setAllUsers(replaceItem(allUsers, updatedUser));
            setFilterUsers(replaceItem(filterUsers, updatedUser));
        } else {
            // just get all users again
            refresh();
        }

        setModalProps(null);
        msgBox("Your request has been successfully completed.");
    };

    useEffect(() => {
        if (loadingState === "init") {
            refresh();
        }
    }, []);

    const byUserFilters = (usr: AdminUserModel) => {
        return (
            (filterUsers.length === 0 || filterUsers.includes(usr))
            && (filterSupervisors.length === 0 || (filterSupervisors.findIndex(sv => sv.id === usr.supervisor?.id) > -1))
            && ((filterDepartments.length === 0 || filterDepartments.includes(usr.department)))
            && ((selectedEmployeeType === null || usr.employeeType === selectedEmployeeType))
        );
    };

    useEffect(() => {
        if (filterUsers.length > 0 || filterSupervisors.length > 0 || filterDepartments.length || 0 || selectedEmployeeType !== null) {
            setFilteredUsers(allUsers.filter(byUserFilters));
        } else {
            setFilteredUsers(allUsers);
        }
        setSelectedUsers([]);

    }, [allUsers, filterUsers, filterSupervisors, filterDepartments, selectedEmployeeType]);

    const numberSort = (a?: number | null, b?: number | null) => {
        if (!a && !b) return 0;

        // nulls first
        if (!a) return -1;
        if (!b) return 1;

        if (a < b) return -1;
        if (a > b) return 1;

        return 0;
    };

    const msgBox = (msg: ReactNode) => {
        setConfirmationProps({
            message: msg,
            options: ["OK"],
            defaultOption: "OK",
            setResult: result => {
                setConfirmationProps(null);
            }
        });
    };

    const ask = (
        msg: React.ReactNode,
        opts: string[],
        def: string,
        defFn: () => void,
        otherFn?: (result: string) => void
    ) => {
        setConfirmationProps({
            message: msg,
            options: opts,
            defaultOption: def,
            setResult: result => {
                setConfirmationProps(null);

                if (result === def) {
                    if (defFn) defFn();
                } else {
                    if (otherFn) otherFn(result);
                }
            }
        });
    };

    const loadUser = (id: number) => {
        return userAdminApi.getAsync(id);
    };

    const saveUser = async (userToSave: AdminUserModel) => {
        let user = await userAdminApi.updateAsync(userToSave);
        await refresh();
        return user;
    };

    const viewUser = (usr: AdminUserModel) => {
        if (usr) {
            setModalProps({
                className: "user-information-dialog",
                body:
                    <DetailFormWrapper
                        module={UserManagementModule}
                        viewName="view"
                        load={() => loadUser(usr.id)}
                        save={saveUser}
                        setConfirmationProps={setConfirmationProps}
                        closeDialog={() => setModalProps(null)}
                        onDataChanged={onDataChanged}
                    />
            });
        }
    };

    const makeUsersInactive = (usersToInactivate: AdminUserModel[]) => {
        changeUserStatus(usersToInactivate, UserStatusEnum.Inacitve);
    };

    const makeUsersActive = (usersToActivate: AdminUserModel[]) => {
        changeUserStatus(usersToActivate, UserStatusEnum.Active);
    };

    const changeUserStatus = (usersToChange: AdminUserModel[], status: UserStatusEnum) => {
        if (usersToChange.length < 1) {
            msgBox(<div>Please select at least one user <br /> to continue.</div>);
            return;
        }

        var nonEmployeesSelected = usersToChange.filter(usr => usr.employeeType !== EmployeeTypeEnum.Contractor).length;

        if (nonEmployeesSelected > 0) {
            msgBox(<div>The selected action can only be <br /> performed on Contractor users.</div>);
            return;
        }

        const users = usersToChange.length === 1 ? "user" : "users";
        const newStatus = status === UserStatusEnum.Active ? "active" : "inactive";

        ask(
            <div>Are you sure you would like to <br /> make the {users} { newStatus} ?</div>,
            ["No", "Yes"],
            "Yes",
            () => {
                const req = {
                    userIds: usersToChange.map(usr => usr.id),
                    status: status
                } as AdminUserSetStatusRequest;

                userAdminApi.setStatus(req).then((result) => {
                    if (result) {
                        refresh();
                        msgBox("You have successfully changed the status of the " + users + ".");
                    }
                });


            }
        );
    }

    const onUserDoubleClick = (usr: AdminUserModel) => {
        viewUser(usr);
    };

    const perform = (actionName: string, user: AdminUserModel) => {
        switch (actionName) {
            case ViewOrEditButtonText:
                return viewUser(user);
        }       
    };

    const checkMinMax = (action: IActionProps) => {
        if (selectedUsers.length === 0 && (action.minSelectedCount ?? 0) > 0) {
            msgBox("Please select one item to continue.");
            return false;
        }

        if (!!action.maxSelectedCount) {
            if (selectedUsers.length > action.maxSelectedCount) {
                if (action.maxSelectedCount === 1) {
                    msgBox(<div>The selected action can only <br /> be performed on a single user.</div>);
                } else {
                    msgBox("Please select " + action.maxSelectedCount + " or fewer items to continue.");
                }

                return false;
            }
        }

        return true;
    };

    const actionClicked = (action: IActionProps) => {
        if (checkMinMax(action)) {
            switch (action.name) {
                case ViewOrEditButtonText:
                    viewUser(selectedUsers[0]);
                    break;
                case MakeInactiveButtonText:
                    makeUsersInactive(selectedUsers.map(u => u));
                    break;
                case MakeActiveButtonText:
                    makeUsersActive(selectedUsers.map(u => u));
                    break;
                case RefreshButtonText:
                    refresh();
                    break;
            }
        }
    };

    return (
        <PortalPage module="administration"
            page={UserManagementModule.className}
            loadingState={loadingState}
            title={UserManagementModule.title}
            route={UserManagementModule.url ?? ""}
            icon={UserManagementModule.icon}
            confirmationProps={confirmationProps}
            modalProps={modalProps} >

            {loadingState === "loading" &&
                <Spinner />
            }

            {loadingState === "ready" && <>

                <UserFilters
                    selectAllUsers={selectAllUsers}
                    setSelectAllUsers={setSelectAllUsers}
                    allUsers={allUsers}
                    currentUser={currentUser}
                    selectedUsers={filterUsers}
                    setSelectedUsers={setFilterUsers}
                    allDepartments={allDepartments}
                    selectedDepartments={filterDepartments}
                    setSelectedDepartments={setFilterDepartments}
                    selectedEmployeeType={selectedEmployeeType}
                    setSelectedEmployeeType={setSelectedEmployeeType}
                    selectedSupervisors={filterSupervisors}
                    setSelectedSupervisors={setFilterSupervisors}
                />

                <ResultHeading text={filteredUsers.length === allUsers.length ? "All users" : "Selected users"} />

                <Toolbar
                    actions={UserManagementModule.actions ?? []}
                    onClick={actionClicked}
                    selectedCount={selectedUsers.length} />

                <DataGrid
                    rows={filteredUsers}
                    nullValue={"-"}
                    selectedItems={selectedUsers}
                    setSelectedItems={setSelectedUsers}
                    selectMode={UserManagementModule.selectMode}
                    pageSize={UserManagementModule.pageSize ?? 20}
                    columns={(UserManagementModule.columns ?? []).filter(c => c.showInGrid).sort((a, b) => numberSort(a.showInGrid, b.showInGrid))}
                    perform={perform}
                    onDoubleClick={onUserDoubleClick} />
            </>
            }
        </PortalPage>
    );
};

export default UserManagementPage;